import React, { useEffect, useMemo, useRef, useState } from 'react';
import HipaaLogo from '@assets/hipaa-logo.svg?react';
import { Box, Slide, Step, StepLabel, Stepper } from '@mui/material';

import { LogoutButton } from '../../../components/Buttons/LogoutButton';
import { CenteredSpinner } from '../../../components/ui/feedback/CenteredSpinner';
import { useEventTracking } from '../../../services/statsig';
import { useGetUserBundlesQuery } from '../store/api';
import { useBundleCatalog } from '../store/hooks';
import type { BillingInterval } from '../types';

import { AddOnSelectionStep } from './AddOnSelectionStep';
import { ConfirmPaymentStep } from './ConfirmPaymentStep';
import { TierSelectionStep } from './TierSelectionStep';
import { TrialEndedBanner } from './TrialEndedBanner';

interface SubscriptionMultiStepFlowProps {
  trialEndDate?: string | Date;
}

export const SubscriptionMultiStepFlow: React.FC<SubscriptionMultiStepFlowProps> = ({
  trialEndDate,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [contentKey, setContentKey] = useState(0);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('left');
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('year'); // Default to annual
  const [selectedTierId, setSelectedTierId] = useState<string | null>(null);
  const [selectedAddOnIds, setSelectedAddOnIds] = useState<Set<string>>(new Set());
  const [hasInitializedFromExisting, setHasInitializedFromExisting] = useState(false);

  const {
    tiers,
    getCompatibleAddOnsForDisplay,
    annualDiscountRange,
    isLoading: isCatalogLoading,
  } = useBundleCatalog();
  const { data: userBundlesData, isLoading: isUserBundlesLoading } = useGetUserBundlesQuery();
  const { subscription } = useEventTracking();

  const flowStartTime = useRef<number>(Date.now());
  const stepStartTime = useRef<number>(Date.now());

  // Refs to capture latest values for cleanup function (avoids stale closure)
  const activeStepRef = useRef(activeStep);
  const selectedTierIdRef = useRef(selectedTierId);
  const selectedAddOnIdsRef = useRef(selectedAddOnIds);

  // Keep refs in sync with state
  activeStepRef.current = activeStep;
  selectedTierIdRef.current = selectedTierId;
  selectedAddOnIdsRef.current = selectedAddOnIds;

  const isLoading = isCatalogLoading || isUserBundlesLoading;

  const isSingleTier = tiers.length === 1;

  const steps = isSingleTier
    ? ['Select Add-ons', 'Confirm & Pay']
    : ['Pick Your Tier', 'Select Add-ons', 'Confirm & Pay'];
  const maxSteps = steps.length;

  // Extract existing bundles from user's subscription
  const existingBundles = useMemo(() => {
    if (!userBundlesData?.bundles) return { tier: null, addOns: [] as string[] };

    const tier = userBundlesData.bundles.find(b => b.bundle_type === 'tier');
    const addOns = userBundlesData.bundles
      .filter(b => b.bundle_type === 'add_on' || b.bundle_type === 'data_security')
      .map(b => b.bundle_id);

    return { tier: tier?.bundle_id ?? null, addOns };
  }, [userBundlesData]);

  // Initialize selections from existing bundles (runs once when data is loaded)
  useEffect(() => {
    if (hasInitializedFromExisting || isLoading) return;

    // For single tier, auto-select the tier
    if (isSingleTier && tiers[0]) {
      setSelectedTierId(tiers[0].bundle_id);
    }
    // Otherwise, use existing tier if available
    else if (existingBundles.tier) {
      setSelectedTierId(existingBundles.tier);
    }

    // Pre-select existing add-ons
    if (existingBundles.addOns.length > 0) {
      setSelectedAddOnIds(new Set(existingBundles.addOns));
    }

    // Calculate initial step based on existing selections
    // For multi-tier flow:
    //   - Has tier + has add-ons → Start at Confirm & Pay (step 2)
    //   - Has tier only → Start at Add-ons (step 1)
    //   - No tier → Start at Tier Selection (step 0)
    // For single-tier flow:
    //   - Has add-ons → Start at Confirm & Pay (step 1)
    //   - No add-ons → Start at Add-ons (step 0)
    if (!isSingleTier) {
      if (existingBundles.tier && existingBundles.addOns.length > 0) {
        setActiveStep(2); // Confirm & Pay
      } else if (existingBundles.tier) {
        setActiveStep(1); // Add-ons
      }
      // else stay at step 0 (Tier Selection)
    } else {
      if (existingBundles.addOns.length > 0) {
        setActiveStep(1); // Confirm & Pay
      }
      // else stay at step 0 (Add-ons)
    }

    setHasInitializedFromExisting(true);
  }, [isLoading, hasInitializedFromExisting, isSingleTier, tiers, existingBundles]);

  // Track flow started and abandoned
  useEffect(() => {
    flowStartTime.current = Date.now();
    stepStartTime.current = Date.now();

    subscription.flowStarted({
      entry_point: trialEndDate ? 'trial_ended' : 'subscription_page',
    });

    return () => {
      // Track abandonment if user leaves before completing payment
      // Use refs to get latest values (avoids stale closure issue)
      const currentStep = activeStepRef.current;
      if (currentStep < maxSteps - 1) {
        subscription.flowAbandoned({
          step_name: steps[currentStep],
          step_number: currentStep,
          time_in_flow: Math.round((Date.now() - flowStartTime.current) / 1000),
          tier_selected: selectedTierIdRef.current !== null,
          addon_count: selectedAddOnIdsRef.current.size,
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeStep]);

  const handleNext = () => {
    if (activeStep < maxSteps - 1) {
      // Track step next click
      subscription.stepNextClicked({
        step_name: steps[activeStep],
        step_number: activeStep,
      });

      // Track step completion
      const timeOnStep = Math.round((Date.now() - stepStartTime.current) / 1000);
      subscription.stepCompleted({
        step_name: steps[activeStep],
        step_number: activeStep,
        time_on_step: timeOnStep,
      });

      setSlideDirection('left');
      setActiveStep(prev => prev + 1);
      setContentKey(prev => prev + 1);
      stepStartTime.current = Date.now();
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      // Track step back click
      subscription.stepBackClicked({
        step_name: steps[activeStep],
        step_number: activeStep,
      });

      setSlideDirection('right');
      setActiveStep(prev => prev - 1);
      setContentKey(prev => prev + 1);
      stepStartTime.current = Date.now();
    }
  };

  const handleSelectTier = (tierId: string) => {
    setSelectedTierId(tierId);
  };

  const handleToggleAddOn = (addOnId: string) => {
    setSelectedAddOnIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(addOnId)) {
        newSet.delete(addOnId);
      } else {
        newSet.add(addOnId);
      }
      return newSet;
    });
  };

  const handleChangeTier = () => {
    setSlideDirection('right');
    setActiveStep(0);
    setContentKey(prev => prev + 1);
  };

  const selectedTier = tiers.find(tier => tier.bundle_id === selectedTierId) || null;

  // Compute filtered add-ons based on selected tier
  const compatibleAddOns = useMemo(
    () => getCompatibleAddOnsForDisplay(selectedTierId),
    [getCompatibleAddOnsForDisplay, selectedTierId],
  );

  const selectedAddOns = compatibleAddOns.filter(addOn => selectedAddOnIds.has(addOn.bundle_id));

  const getStepContent = (step: number): React.ReactElement => {
    if (isSingleTier) {
      switch (step) {
        case 0:
          return (
            <AddOnSelectionStep
              isSingleTier
              selectedTier={selectedTier}
              addOns={compatibleAddOns}
              selectedAddOnIds={selectedAddOnIds}
              billingInterval={billingInterval}
              onToggleAddOn={handleToggleAddOn}
              onBillingIntervalChange={setBillingInterval}
              onChangeTier={handleChangeTier}
              onNext={handleNext}
            />
          );
        case 1:
          return (
            <ConfirmPaymentStep
              selectedTier={selectedTier}
              selectedAddOns={selectedAddOns}
              billingInterval={billingInterval}
              onBillingIntervalChange={setBillingInterval}
              onChangeTier={handleChangeTier}
            />
          );
        default:
          return <div>Unknown step</div>;
      }
    }

    switch (step) {
      case 0:
        return (
          <TierSelectionStep
            tiers={tiers}
            selectedTierId={selectedTierId}
            billingInterval={billingInterval}
            onSelectTier={handleSelectTier}
            onBillingIntervalChange={setBillingInterval}
            onNext={handleNext}
            discountRange={annualDiscountRange}
          />
        );
      case 1:
        return (
          <AddOnSelectionStep
            selectedTier={selectedTier}
            addOns={compatibleAddOns}
            selectedAddOnIds={selectedAddOnIds}
            billingInterval={billingInterval}
            onToggleAddOn={handleToggleAddOn}
            onBillingIntervalChange={setBillingInterval}
            onChangeTier={handleChangeTier}
            onNext={handleNext}
          />
        );
      case 2:
        return (
          <ConfirmPaymentStep
            selectedTier={selectedTier}
            selectedAddOns={selectedAddOns}
            billingInterval={billingInterval}
            onBillingIntervalChange={setBillingInterval}
            onChangeTier={handleChangeTier}
          />
        );
      default:
        return <div>Unknown step</div>;
    }
  };

  if (isLoading) {
    return <CenteredSpinner message="Loading subscription options..." />;
  }

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fafafa',
      }}
    >
      {/* Trial Ended Banner */}
      {trialEndDate && <TrialEndedBanner trialEndDate={trialEndDate} />}

      {/* Main content area with stepper and steps */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: { xs: 3, sm: 4 },
          pb: 4,
          px: 2,
          overflowY: 'auto',
        }}
      >
        {/* Stepper */}
        <Box sx={{ width: '100%', maxWidth: '800px', mb: 4 }}>
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{
              '& .MuiStepConnector-line': {
                borderTopWidth: 3,
              },
              '& .MuiStepLabel-label': {
                fontSize: '0.95rem',
                fontWeight: 500,
                display: { xs: 'none', sm: 'block' },
              },
            }}
          >
            {steps.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Step content with slide transition - centered vertically */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Slide
            direction={slideDirection}
            in={true}
            mountOnEnter
            unmountOnExit
            key={contentKey}
            style={{ transformOrigin: 'right' }}
          >
            <Box
              sx={{
                width: '100%',
              }}
            >
              {getStepContent(activeStep)}
            </Box>
          </Slide>
        </Box>
      </Box>

      {/* Footer with HIPAA logo and logout */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 4,
          pb: 4,
          mt: 'auto',
        }}
      >
        <LogoutButton size="small" />
        <HipaaLogo height={43} fill="#C8ADE1" aria-label="HIPAA Compliant" />
      </Box>
    </Box>
  );
};
